'use client';

import Image from 'next/image';
import { useTranslations, useLocale } from 'next-intl';
import { useAuthStore } from '@/stores/auth';
import { useNotificationsStore } from '@/stores/notifications';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, usePathname, useRouter } from '../../../i18n/routing';
import { useParams } from 'next/navigation';
import {
  LayoutDashboard,
  Ship,
  Megaphone,
  LogIn,
  CreditCard,
  Bell,
  Anchor,
  Building2,
  Moon,
  Sun,
  Languages,
  Menu,
  X,
} from 'lucide-react';
import { RoleSwitcher } from './RoleSwitcher';

export function TheAppBar() {
  const t = useTranslations();
  const locale = useLocale();
  const authStore = useAuthStore();
  const notificationsStore = useNotificationsStore();
  const pathname = usePathname();
  const router = useRouter();
  const params = useParams();
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [notificationsMenuOpen, setNotificationsMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  // Initialize from localStorage if available, otherwise default to false
  // During SSR, window is undefined, so we default to false and update in useEffect
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  // Coming-soon mode is enabled via environment variable (build-time)
  const isComingSoonMode = process.env.NEXT_PUBLIC_COMING_SOON_MODE === 'true';

  useEffect(() => {
    setMounted(true);
    // Sync with the current DOM state (set by the script in layout.tsx)
    // This ensures we're in sync with what the script already set
    const isCurrentlyDark = document.documentElement.classList.contains('dark');
    setIsDarkMode(isCurrentlyDark);

    // Also sync with localStorage to ensure consistency
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const shouldBeDark = savedTheme === 'dark' || (!savedTheme && prefersDark);

    // Only update if there's a mismatch
    if (isCurrentlyDark !== shouldBeDark) {
      setIsDarkMode(shouldBeDark);
      if (shouldBeDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }

    // Mobile detection - use lg breakpoint (1024px) instead of md (768px)
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const toggleDarkMode = () => {
    // Read current state from DOM to ensure accuracy
    const currentIsDark = document.documentElement.classList.contains('dark');
    const newDarkMode = !currentIsDark;

    // Update DOM immediately (before state update for instant feedback)
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }

    // Update state
    setIsDarkMode(newDarkMode);
  };


  const handleLogout = async () => {
    await authStore.logout(true);
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
  };

  const switchLocale = (newLocale: string) => {
    setLangMenuOpen(false);
    // Use next-intl router to properly handle locale switching with dynamic routes
    // This ensures params like [slug] are preserved correctly
    // @ts-expect-error -- We know the params match the route requirements
    router.replace({ pathname, params }, { locale: newLocale });
  };

  const handleNotificationClick = (notification: { id: string; read: boolean; link?: string | null }) => {
    if (!notification.read) {
      notificationsStore.markAsRead(notification.id);
    }
    if (notification.link) {
      window.location.href = notification.link;
    }
    setNotificationsMenuOpen(false);
  };

  const isActive = (path: string) => {
    if (path === '/dashboard') return pathname.includes('/dashboard');
    if (path === '/cruises' && !pathname.includes('/cruises/create')) return pathname.includes('/cruises');
    return pathname.includes(path);
  };

  return (
    <nav className="sticky top-0 z-50 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 border-b border-slate-200 dark:border-slate-800 w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
        <div className="flex items-center justify-between w-full">
          {/* Logo Section */}
          <Link href="/" className="flex items-center text-slate-900 dark:text-slate-100 no-underline flex-shrink-0 min-w-0">
            {mounted && <Anchor size={32} className="text-blue-600 dark:text-blue-400 mr-2 flex-shrink-0" />}
            <h1 className="text-xl font-semibold font-mono tracking-wide m-0 whitespace-nowrap">
              {t('common.appName')}
            </h1>
          </Link>

          {/* Middle Navigation Links */}
          {!isMobile && !isComingSoonMode && (
            <div className="hidden lg:flex items-center lg:gap-x-2 xl:gap-x-3 flex-1 justify-center">
              {authStore.isLoggedIn ? (
                <>
                  <Link 
                    href="/dashboard" 
                    className={`text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white transition-colors no-underline flex items-center gap-2 whitespace-nowrap px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800/50 ${isActive('/dashboard') ? 'dark:text-white font-bold' : ''}`}
                  >
                    {mounted && <LayoutDashboard size={20} />}
                    <span>{t('common.dashboard')}</span>
                  </Link>
                  <Link 
                    href="/cruises" 
                    className={`text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white transition-colors no-underline flex items-center gap-2 whitespace-nowrap px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800/50 ${isActive('/cruises') ? 'dark:text-white font-bold' : ''}`}
                  >
                    {mounted && <Ship size={20} />}
                    <span>{t('common.browseCruises')}</span>
                  </Link>
                  <Link
                    href="/organizations"
                    className={`text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white transition-colors no-underline flex items-center gap-2 whitespace-nowrap px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800/50 ${isActive('/organizations') ? 'dark:text-white font-bold' : ''}`}
                  >
                    {mounted && <Building2 size={20} />}
                    <span>Cruise Organizers</span>
                  </Link>
                  <Link
                    href="/cruises/create"
                    className={`text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white transition-colors no-underline flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800/50 ${isActive('/cruises/create') ? 'dark:text-white font-bold' : ''}`}
                  >
                    {mounted && <Megaphone size={20} />}
                    <span className="hidden xl:inline whitespace-nowrap">{t('userMenu.publishCta')}</span>
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    href="/cruises"
                    className={`text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white transition-colors no-underline flex items-center gap-2 whitespace-nowrap px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800/50 ${isActive('/cruises') ? 'dark:text-white font-bold' : ''}`}
                  >
                    {mounted && <Ship size={20} />}
                    <span>{t('common.browseCruises')}</span>
                  </Link>
                  <Link
                    href="/organizations"
                    className={`text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white transition-colors no-underline flex items-center gap-2 whitespace-nowrap px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800/50 ${isActive('/organizations') ? 'dark:text-white font-bold' : ''}`}
                  >
                    {mounted && <Building2 size={20} />}
                    <span>Cruise Organizers</span>
                  </Link>
                </>
              )}
            </div>
          )}

          {/* Right Side Actions */}
          <div className="flex items-center gap-4 flex-shrink-0">
            {/* Mobile Menu Button */}
            {isMobile && !isComingSoonMode && (
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-md transition-all duration-200 hover:scale-105 active:scale-95 shadow-md"
                aria-label="open navigation menu"
              >
                {mounted && <Menu size={24} />}
              </button>
            )}

            {/* Right Side Elements Wrapper - Desktop */}
            <div className="hidden lg:flex items-center gap-4">
              {/* Role Switcher - Only show when logged in */}
              {authStore.isLoggedIn && (
                <>
                  {/* Vertical Divider */}
                  <div className="h-6 w-px bg-slate-200 dark:bg-slate-700" />
                  <div className="flex items-center min-w-fit">
                    <RoleSwitcher />
                  </div>
                </>
              )}

              {/* Dark Mode Toggle */}
              <button
                onClick={toggleDarkMode}
                aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                className="p-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-slate-900 dark:text-yellow-400 flex items-center"
              >
                {mounted && (isDarkMode ? <Sun size={20} /> : <Moon size={20} />)}
              </button>

              {/* Language Switcher */}
              <div className="relative">
                <button
                  onClick={() => setLangMenuOpen(!langMenuOpen)}
                  className="text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white px-3 py-2 rounded-md transition-colors flex items-center gap-2"
                >
                  {mounted && <Languages size={18} />}
                  <span>{locale.toUpperCase()}</span>
                </button>
              {langMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setLangMenuOpen(false)} />
                  <div className="absolute right-0 mt-2 w-32 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md shadow-lg z-50">
                    <button
                      onClick={() => switchLocale('pl')}
                      className={`w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors ${locale === 'pl' ? 'bg-slate-100 dark:bg-slate-800 font-semibold' : ''}`}
                    >
                      Polski
                    </button>
                    <button
                      onClick={() => switchLocale('en')}
                      className={`w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors ${locale === 'en' ? 'bg-slate-100 dark:bg-slate-800 font-semibold' : ''}`}
                    >
                      English
                    </button>
                  </div>
                </>
              )}
              </div>

              {/* Notifications Icon - Only for admin users */}
              {authStore.isLoggedIn && authStore.user?.is_master_admin && (
                <div className="relative">
                  <button
                    onClick={() => setNotificationsMenuOpen(!notificationsMenuOpen)}
                    className="p-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-slate-600 dark:text-slate-300 relative flex items-center"
                    aria-label={t('notifications.title')}
                  >
                    {mounted && <Bell size={20} />}
                    {notificationsStore.unreadCount > 0 && (
                      <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                        {notificationsStore.unreadCount > 9 ? '9+' : notificationsStore.unreadCount}
                      </span>
                    )}
                  </button>
                  {notificationsMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setNotificationsMenuOpen(false)} />
                      <div className="absolute right-0 mt-2 w-80 max-h-96 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md shadow-lg z-50 overflow-hidden flex flex-col">
                        <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
                          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{t('notifications.title')}</h3>
                          {notificationsStore.unreadCount > 0 && (
                            <button
                              onClick={() => notificationsStore.markAllAsRead()}
                              className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              {t('notifications.markAllRead')}
                            </button>
                          )}
                        </div>
                        <div className="overflow-y-auto max-h-80">
                          {notificationsStore.notifications.length === 0 ? (
                            <div className="p-6 text-center">
                              <p className="text-sm text-slate-500 dark:text-slate-400">
                                {t('notifications.empty')}
                              </p>
                            </div>
                          ) : (
                            <ul className="divide-y divide-slate-200 dark:divide-slate-800">
                              {notificationsStore.notifications.slice(0, 10).map((notification) => (
                                <li key={notification.id}>
                                  <button
                                    onClick={() => handleNotificationClick(notification)}
                                    className={`w-full text-left p-3 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors ${
                                      !notification.read ? 'bg-slate-50 dark:bg-slate-800 border-l-4 border-blue-600' : ''
                                    }`}
                                  >
                                    <p className={`text-sm ${notification.read ? 'font-normal' : 'font-bold'} text-slate-900 dark:text-slate-100 truncate`}>
                                      {notification.title}
                                    </p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-1">
                                      {notification.message}
                                    </p>
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        {notificationsStore.notifications.length > 0 && (
                          <div className="p-2 border-t border-slate-200 dark:border-slate-800">
                            <Link
                              href="/notifications"
                              onClick={() => setNotificationsMenuOpen(false)}
                              className="block w-full text-center text-sm text-blue-600 dark:text-blue-400 hover:underline py-2"
                            >
                              {t('notifications.viewAll')}
                            </Link>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Login/Register Buttons */}
              {!authStore.isLoggedIn && (
                <div className="flex items-center gap-2">
                  <Link
                    href="/login"
                    className="px-3 py-1.5 lg:px-4 lg:py-2 border border-slate-300 dark:border-slate-700 rounded-md text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors no-underline flex items-center gap-2 whitespace-nowrap"
                  >
                    {mounted && <LogIn size={18} />}
                    <span className="hidden xl:inline">{t('common.login')}</span>
                  </Link>
                  <Link
                    href="/register"
                    className="px-3 py-1.5 lg:px-4 lg:py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors no-underline whitespace-nowrap"
                  >
                    <span className="hidden xl:inline">{t('common.register')}</span>
                    <span className="xl:hidden">{t('common.register')}</span>
                  </Link>
                </div>
              )}

              {/* User Avatar */}
              {authStore.isLoggedIn && (
                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="p-0 rounded-full hover:ring-2 ring-slate-300 dark:ring-slate-700 transition-all flex items-center"
                  >
                    {authStore.user?.avatar_url ? (
                      <Image
                        src={authStore.user.avatar_url}
                        alt={authStore.user.full_name || 'User'}
                        width={40}
                        height={40}
                        className="w-10 h-10 rounded-full"
                        unoptimized
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-blue-600 dark:bg-blue-500 flex items-center justify-center text-white font-semibold">
                        {authStore.user?.full_name?.charAt(0) || 'U'}
                      </div>
                    )}
                  </button>
                {userMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                    <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md shadow-lg z-50">
                      <Link
                        href="/settings"
                        onClick={() => setUserMenuOpen(false)}
                        className="block px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-slate-900 dark:text-slate-100 no-underline"
                      >
                        {t('settingsPage.title')}
                      </Link>
                      <Link
                        href="/dashboard"
                        onClick={() => setUserMenuOpen(false)}
                        className="block px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-slate-900 dark:text-slate-100 no-underline"
                      >
                        {t('common.dashboard')}
                      </Link>
                      <div className="border-t border-slate-200 dark:border-slate-800 my-1" />
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-slate-900 dark:text-slate-100"
                      >
                        {t('common.logout')}
                      </button>
                    </div>
                  </>
                )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Menu Drawer - portaled to body so fixed positioning is viewport-relative (avoids shift from ancestor overflow/transform) */}
        {mounted && mobileMenuOpen && !isComingSoonMode && createPortal(
          <>
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={() => setMobileMenuOpen(false)} />
            <div className="fixed right-0 top-0 left-auto h-full max-h-[100vh] w-72 max-w-[90vw] bg-white/95 dark:bg-slate-950/95 backdrop-blur-md text-slate-900 dark:text-slate-100 shadow-xl z-50 flex flex-col border-l border-slate-200/50 dark:border-slate-800/50" style={{ right: 0, left: 'auto' }}>
              <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center flex-shrink-0">
                <h2 className="text-lg font-semibold">{t('common.appName')}</h2>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              <nav className="flex flex-col flex-1 min-h-0">
                <div className="flex-1 min-h-0 overflow-y-auto">
                  {authStore.isLoggedIn ? (
                    <>
                      {/* Role Switcher - Mobile: align dropdown left so it stays inside the drawer */}
                      {authStore.isLoggedIn && (
                        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800">
                          <RoleSwitcher alignDropdown="left" />
                        </div>
                      )}
                      <Link
                        href="/dashboard"
                        onClick={() => setMobileMenuOpen(false)}
                        className="px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center gap-3 no-underline text-slate-900 dark:text-slate-100"
                      >
                        {mounted && <LayoutDashboard size={20} />}
                        <span>{t('common.dashboard')}</span>
                      </Link>
                      <Link
                        href="/cruises"
                        onClick={() => setMobileMenuOpen(false)}
                        className="px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center gap-3 no-underline text-slate-900 dark:text-slate-100"
                      >
                        {mounted && <Ship size={20} />}
                        <span>{t('common.browseCruises')}</span>
                      </Link>
                      <Link
                        href="/organizations"
                        onClick={() => setMobileMenuOpen(false)}
                        className="px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center gap-3 no-underline text-slate-900 dark:text-slate-100"
                      >
                        {mounted && <Building2 size={20} />}
                        <span>Cruise Organizers</span>
                      </Link>
                      <Link
                        href="/cruises/create"
                        onClick={() => setMobileMenuOpen(false)}
                        className="px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center gap-3 no-underline text-slate-900 dark:text-slate-100"
                      >
                        {mounted && <Megaphone size={20} />}
                        <span>{t('userMenu.publishCta')}</span>
                      </Link>
                      <Link
                        href="/settings"
                        onClick={() => setMobileMenuOpen(false)}
                        className="px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center gap-3 no-underline text-slate-900 dark:text-slate-100"
                      >
                        {authStore.user?.avatar_url ? (
                          <Image
                            src={authStore.user.avatar_url}
                            alt={authStore.user.full_name || 'User'}
                            width={24}
                            height={24}
                            className="w-6 h-6 rounded-full"
                            unoptimized
                          />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-blue-600 dark:bg-blue-500 flex items-center justify-center text-white text-xs font-semibold">
                            {authStore.user?.full_name?.charAt(0) || 'U'}
                          </div>
                        )}
                        <span>{t('settingsPage.title')}</span>
                      </Link>
                    </>
                  ) : (
                    <>
                      <Link
                        href="/cruises"
                        onClick={() => setMobileMenuOpen(false)}
                        className="px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center gap-3 no-underline text-slate-900 dark:text-slate-100"
                      >
                        {mounted && <Ship size={20} />}
                        <span>{t('common.browseCruises')}</span>
                      </Link>
                      <Link
                        href="/organizations"
                        onClick={() => setMobileMenuOpen(false)}
                        className="px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center gap-3 no-underline text-slate-900 dark:text-slate-100"
                      >
                        {mounted && <Building2 size={20} />}
                        <span>Cruise Organizers</span>
                      </Link>
                      <Link
                        href="/login"
                        onClick={() => setMobileMenuOpen(false)}
                        className="px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center gap-3 no-underline text-slate-900 dark:text-slate-100"
                      >
                        {mounted && <LogIn size={20} />}
                        <span>{t('common.login')}</span>
                      </Link>
                      <Link
                        href="/register"
                        onClick={() => setMobileMenuOpen(false)}
                        className="px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center gap-3 no-underline text-slate-900 dark:text-slate-100"
                      >
                        {mounted && <CreditCard size={20} />}
                        <span>{t('common.register')}</span>
                      </Link>
                    </>
                  )}
                </div>
                <div className="flex-shrink-0 border-t border-slate-200 dark:border-slate-800 pt-2 pb-2">
                  {authStore.isLoggedIn && (
                    <button
                      onClick={handleLogout}
                      className="px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center gap-3 text-left w-full text-slate-900 dark:text-slate-100"
                    >
                      {mounted && <LogIn size={20} className="rotate-180" />}
                      <span>{t('common.logout')}</span>
                    </button>
                  )}
                  <button
                    onClick={toggleDarkMode}
                    className="px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center gap-3 text-left w-full text-slate-900 dark:text-yellow-400"
                  >
                    {mounted && (isDarkMode ? <Sun size={20} /> : <Moon size={20} />)}
                    <span>{isDarkMode ? t('common.lightMode') || 'Light Mode' : t('common.darkMode') || 'Dark Mode'}</span>
                  </button>
                  <button
                    onClick={() => switchLocale(locale === 'pl' ? 'en' : 'pl')}
                    className="px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center gap-3 text-left w-full text-slate-900 dark:text-slate-100"
                  >
                    {mounted && <Languages size={20} />}
                    <span>{locale === 'pl' ? 'Switch to English' : 'Przełącz na Polski'}</span>
                  </button>
                </div>
              </nav>
            </div>
          </>,
          document.body
        )}
      </div>
    </nav>
  );
}
